<script lang="ts" module>
	import type { Snippet } from 'svelte';
	import type { ClassValue, HTMLInputAttributes } from 'svelte/elements';
	import type { VariantProps } from 'tailwind-variants';
	import { tv } from '../tv';
	import { CONTAINER_COLOR_VARIANTS, RADII_VARIANTS } from '../variants';

	export const inputVariants = tv({
		base: 'flex flex-wrap items-center bg-(--v-container) text-(--v-on-container) has-[input:focus]:[box-shadow:inset_0_0_0_var(--border-width-b600)_var(--v-container-line)] supports-[not_selector(:has(input:focus))]:focus-within:[box-shadow:inset_0_0_0_var(--border-width-b600)_var(--v-container-line)]',
		variants: {
			variant: CONTAINER_COLOR_VARIANTS,
			size: {
				'300': 'h-[2rem] gap-s100 px-s200 [&>input]:text-t200',
				'400': 'h-[2.5rem] gap-s200 px-s300 [&>input]:text-t300',
				'500': 'h-[3rem] gap-s300 px-s400 [&>input]:text-t400'
			},
			outlined: {
				true: '[box-shadow:inset_0_0_0_var(--border-width-b400)_var(--v-container-line)]'
			},
			radii: RADII_VARIANTS
		},
		defaultVariants: { variant: 'Background', size: '400', radii: '400' }
	});

	export interface InputProps
		extends Omit<HTMLInputAttributes, 'class' | 'size'>, VariantProps<typeof inputVariants> {
		after?: Snippet;
		before?: Snippet;
		class?: ClassValue;
		inputSize?: number;
		ref?: HTMLInputElement | null;
	}
</script>

<script lang="ts">
	let {
		after,
		before,
		class: className,
		inputSize = 1,
		outlined,
		radii,
		ref = $bindable(null),
		size,
		style,
		value = $bindable(),
		variant,
		...rest
	}: InputProps = $props();

	export function focus(): void {
		ref?.focus();
	}
</script>

<div
	class={[inputVariants({ outlined, radii, size, variant }), className]}
	data-ui-after={after ? true : undefined}
	data-ui-before={before ? true : undefined}
	{style}
>
	{@render before?.()}
	<input
		bind:this={ref}
		bind:value
		class="min-w-s0 placeholder:opacity-placeholder disabled-state h-full grow bg-transparent placeholder:text-inherit focus:outline-none"
		size={inputSize}
		{...rest}
	/>
	{@render after?.()}
</div>
